import React, { useState, useEffect, useCallback } from 'react';
import { openAuthPopup, retryAuthPopup, AuthPopupClosedError } from '../utils/iframe';
import { clearAllCookies } from '../utils/cookies';

interface IframeAuthDialogProps {
  onAuthComplete: () => void;
  authAttempts: number;
}

const IframeAuthDialog: React.FC<IframeAuthDialogProps> = ({ onAuthComplete, authAttempts }) => {
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Distinguishes "the rep closed the window" from a real failure. The
  // former is recoverable by trying again, so we offer that instead of
  // dead-ending on an error panel.
  const [wasDismissed, setWasDismissed] = useState(false);

  const handleAuthenticate = useCallback(async () => {
    setIsAuthenticating(true);
    setError(null);
    setWasDismissed(false);

    // On retry, clear cookies first and drive the flow through the
    // cookie-clear endpoint. Both paths share one popup runner (escalation,
    // timeout, premature-close handling) so they can't drift apart.
    const isRetry = authAttempts > 0;
    if (isRetry) {
      clearAllCookies();
    }

    try {
      await (isRetry ? retryAuthPopup() : openAuthPopup());
      onAuthComplete();
    } catch (err) {
      // A dismissed popup is the common case (reps reflexively close it) and
      // is fully recoverable — surface a way back rather than a hard error.
      if (err instanceof AuthPopupClosedError) {
        setWasDismissed(true);
      } else {
        setError(isRetry ? 'Authentication failed after retry' : 'Authentication failed');
      }
      setIsAuthenticating(false);
    }
  }, [authAttempts, onAuthComplete]);

  // Auto-trigger popup on mount
  useEffect(() => {
    handleAuthenticate();
    // Intentionally mount-only: re-running on every `handleAuthenticate`
    // identity change would reopen the popup mid-flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isAuthenticating && !error && !wasDismissed) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px'
      }}>
        Signing you in...
      </div>
    );
  }

  const isDismissal = wasDismissed && !error;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '8px',
        maxWidth: '400px',
        textAlign: 'center',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <h2 style={{ marginBottom: '1rem' }}>
          {isDismissal ? 'Sign-in was interrupted' : 'Authentication Failed'}
        </h2>
        <div style={{
          backgroundColor: isDismissal ? '#eef4ff' : '#fee',
          color: isDismissal ? '#234' : '#c33',
          padding: '0.5rem',
          borderRadius: '4px',
          fontSize: '0.9rem'
        }}>
          {isDismissal
            ? 'The sign-in window closed before it finished. It signs you in automatically and closes on its own — you can leave it alone.'
            : error}
        </div>
        <button
          type="button"
          onClick={handleAuthenticate}
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1.25rem',
            fontSize: '0.95rem',
            border: 'none',
            borderRadius: '4px',
            backgroundColor: '#0972d3',
            color: '#fff',
            cursor: 'pointer'
          }}
        >
          Try again
        </button>
      </div>
    </div>
  );
};

export default IframeAuthDialog;
